import React from 'react';
import { Link } from 'react-router-dom';
import { useCategories } from '../../lib/taxonomy';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import branding from '../../config/branding';

export const Footer = () => {
  const { t, isHindi } = useLanguage();
  const { isAuthenticated } = useAuth();

  const { topLevel, pathFor } = useCategories();
  const categories = topLevel.map((c) => ({ id: c.id, name: isHindi ? c.name_hi : c.name, to: pathFor(c.id) }));

  const font = isHindi ? 'font-hindi' : '';
  const linkClass = `inline-flex items-center min-h-[32px] font-ui text-[13px] text-[var(--ink-soft)] hover:underline underline-offset-4 ${font}`;
  const headingClass = `kicker text-[var(--ink)] mb-2 ${font}`;

  const quickLinks = [
    { to: '/', label: t('home') },
    { to: '/login', label: t('login') },
    { to: '/dashboard', label: t('dashboard') },
    isAuthenticated && { to: '/leaderboard', label: isHindi ? 'लीडरबोर्ड' : 'Leaderboard' },
  ].filter(Boolean);

  return (
    <footer className="mt-16 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="double-rule" />

        {/* Nameplate */}
        <div className="pt-6 pb-4">
          <Link to="/" className="inline-block text-[var(--ink)] leading-none" aria-label={branding.name}>
            <span className={`text-4xl font-masthead ${isHindi ? 'font-hindi' : ''}`}>
              {isHindi ? branding.nameHi : branding.name}
            </span>
          </Link>
        </div>

        {/* Link columns */}
        <div className="grid grid-cols-2 md:grid-cols-12 gap-x-6 gap-y-8 py-6 border-t border-[var(--rule)]">
          <div className="col-span-2 md:col-span-4">
            <h4 className={headingClass}>{isHindi ? 'हमारे बारे में' : 'About'}</h4>
            <p className={`story-summary text-sm mb-4 ${font}`}>
              {isHindi ? branding.descriptionHi : branding.description}
            </p>
            <ul className={`font-ui text-[13px] text-[var(--ink-muted)] space-y-1 ${font}`}>
              <li>{isHindi ? branding.addressHi : branding.address}</li>
              <li><a href={`mailto:${branding.email}`} className="hover:underline underline-offset-4">{branding.email}</a></li>
              <li>{branding.phone}</li>
            </ul>
          </div>

          <div className="col-span-1 md:col-span-3 md:border-l md:border-[var(--rule)] md:pl-6">
            <h4 className={headingClass}>{isHindi ? 'सेक्शन' : 'Sections'}</h4>
            <ul>
              {categories.map((cat) => (
                <li key={cat.id}><Link to={cat.to} className={linkClass}>{cat.name}</Link></li>
              ))}
            </ul>
          </div>

          <div className="col-span-1 md:col-span-2 md:border-l md:border-[var(--rule)] md:pl-6">
            <h4 className={headingClass}>{isHindi ? 'त्वरित लिंक' : 'Quick Links'}</h4>
            <ul>
              {quickLinks.map(({ to, label }) => (
                <li key={to}><Link to={to} className={linkClass}>{label}</Link></li>
              ))}
            </ul>
          </div>

          <div className="col-span-2 md:col-span-3 md:border-l md:border-[var(--rule)] md:pl-6">
            <h4 className={headingClass}>{isHindi ? 'कानूनी' : 'Legal'}</h4>
            <ul className="grid grid-cols-2 md:grid-cols-1">
              {[t('aboutUs'), t('contactUs'), t('privacyPolicy'), t('termsOfService')].map((item) => (
                <li key={item}>
                  <span className={`${linkClass} cursor-default hover:no-underline`}>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className={`py-5 border-t border-[var(--rule)] text-center md:text-left font-ui text-xs text-[var(--ink-muted)] ${font}`}>
          <span>© {new Date().getFullYear()} {isHindi ? branding.nameHi : branding.name}</span>
        </div>
      </div>
    </footer>
  );
};
